import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import {
  Search,
  Play,
  History,
  Atom,
  ChevronRight,
  Zap,
  ShieldAlert,
  BarChart3,
  Cpu,
  Search as SearchIcon,
  Clock,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Info,
  CheckCircle2,
  Download,
  Save,
  BookMarked,
  Bell,
  ArrowRightLeft,
  Maximize2,
  Filter,
  Loader2,
  ChevronDown,
  Target,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { OwlIcon, ElephantIcon, BrainIcon, CompassIcon } from './AIIcons';
import { motion, AnimatePresence } from 'motion/react';
import { cn, logInteraction } from '../lib/utils';
import { runCouncilDebate, CouncilAnalysis, TranscriptCard } from '../services/aiCouncilService';
import { useCouncilStore } from '../hooks/useCouncilStore';
import type { NormalizedCouncilResult, CouncilDeliberation } from '../types/council';
import { pollDeliberationUntilDone } from '../services/councilAdapter';
import { safeFormatTimestamp } from '../lib/accountFormatters';

// --- Types ---
type DebateStage = 'INTAKE' | 'ANALYSIS' | 'EVALUATION' | 'CHALLENGE' | 'SYNTHESIS' | 'DECISION';

const STAGES: DebateStage[] = ['INTAKE', 'ANALYSIS', 'EVALUATION', 'CHALLENGE', 'SYNTHESIS', 'DECISION'];

const MODEL_CONFIG: Record<string, { color: string, icon: any, tooltip: string }> = {
  'GPT': {
    color: 'text-purple-500 bg-purple-500/10 border-purple-500/20',
    icon: BrainIcon,
    tooltip: 'GPT — AI intelligence engine'
  },
  'Claude': {
    color: 'text-amber-500 bg-amber-500/10 border-amber-500/20',
    icon: OwlIcon,
    tooltip: 'Claude — AI reasoning engine'
  },
  'Gemini': {
    color: 'text-blue-500 bg-blue-500/10 border-blue-500/20',
    icon: ElephantIcon,
    tooltip: 'Gemini — AI knowledge engine'
  },
  'Perplexity': {
    color: 'text-teal-500 bg-teal-500/10 border-teal-500/20',
    icon: CompassIcon,
    tooltip: 'Perplexity — AI search engine'
  },
  'Synthesizer': {
    color: 'text-slate-500 bg-slate-500/10 border-slate-500/20',
    icon: Zap,
    tooltip: 'Synthesizer — Final decision engine'
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  COUNCIL GATEWAY RESULT PANEL
// ═══════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════
//  COUNCIL DECISION CARD (2026-05-19 two-phase async deliberation)
//  Renders "Deliberating…" while polling /deliberate, then swaps in the
//  full structured advisory decision (BUY/SELL/HOLD/WATCHLIST/NO_TRADE/BLOCKED
//  + agreements + disagreements + supporting evidence + Perplexity research).
//  If polling fails or times out, falls back to the seed deterministic
//  decision and shows a small "deliberation unavailable" note rather than
//  breaking the page.
// ═══════════════════════════════════════════════════════════════════════

const DECISION_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  BUY:       { bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500/30' },
  SELL:      { bg: 'bg-rose-500/10',    text: 'text-rose-600',    border: 'border-rose-500/30' },
  HOLD:      { bg: 'bg-blue-500/10',    text: 'text-blue-600',    border: 'border-blue-500/30' },
  WATCHLIST: { bg: 'bg-amber-500/10',   text: 'text-amber-600',   border: 'border-amber-500/30' },
  NO_TRADE:  { bg: 'bg-zinc-500/10',    text: 'text-zinc-500',    border: 'border-zinc-500/30' },
  BLOCKED:   { bg: 'bg-zinc-500/10',    text: 'text-zinc-500',    border: 'border-zinc-500/30' },
};

const CouncilDecisionCard = ({
  analysisId,
  deliberationRequestId,
  seedDeliberation,
  onDeliberationUpdate,
}: {
  analysisId: string;
  deliberationRequestId: string | undefined;
  seedDeliberation: CouncilDeliberation | null;
  onDeliberationUpdate?: (d: CouncilDeliberation | null) => void;
}) => {
  const [deliberation, setDeliberation] = useState<CouncilDeliberation | null>(seedDeliberation);
  // 2026-05-20 council-repair: lift deliberation state to parent so the
  // top-of-page "Analysis Summary" panel can replace the initial readout
  // with the deliberated decisionReason once /deliberate completes.
  useEffect(() => { if (onDeliberationUpdate) onDeliberationUpdate(deliberation); }, [deliberation, onDeliberationUpdate]);
  const [pollAttempts, setPollAttempts] = useState(0);
  const lastAnalysisIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Re-run polling whenever a new analysis lands (different analysisId).
    if (!deliberationRequestId) {
      setDeliberation(seedDeliberation);
      return;
    }
    if (lastAnalysisIdRef.current === analysisId) {
      // Same analysis — don't restart the poll loop. (Avoids double-poll
      // on re-renders.)
      return;
    }
    lastAnalysisIdRef.current = analysisId;
    setDeliberation(seedDeliberation);
    setPollAttempts(0);

    const abort = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        const final = await pollDeliberationUntilDone(deliberationRequestId, {
          intervalMs: 1500,
          totalBudgetMs: 30000,
          onUpdate: (d) => {
            if (cancelled) return;
            setDeliberation(d);
            setPollAttempts((n) => n + 1);
          },
          abortSignal: abort.signal,
        });
        if (!cancelled) setDeliberation(final);
      } catch {
        // pollDeliberationUntilDone swallows network errors and returns a
        // synthetic 'unknown' status — only an unexpected throw lands here.
      }
    })();
    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [analysisId, deliberationRequestId, seedDeliberation]);

  const d = deliberation;
  const decision = (d?.finalDecision || 'WATCHLIST').toUpperCase();
  const isPending = d?.status === 'pending';
  const isUnavailable = d?.status === 'unavailable';
  const colors = DECISION_COLORS[decision] ?? DECISION_COLORS.WATCHLIST;
  const conf = d?.confidence;
  const confText = typeof conf === 'number' ? `${Math.round(conf)}%` : '—';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="council-decision-card"
      className="bg-[var(--card-bg)] border-2 border-[var(--card-border)] rounded-[2rem] p-8 shadow-2xl relative overflow-hidden"
    >
      <div className="relative z-10 space-y-6">
        {/* Header: Decision + Confidence + Status pill */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">
              Council Decision
            </p>
            <div className="flex items-center gap-3 mt-2">
              <div
                className={cn(
                  'px-4 py-2 rounded-xl border font-black uppercase italic text-lg',
                  colors.bg, colors.text, colors.border,
                )}
                data-testid="council-decision-final"
              >
                {isPending ? 'Deliberating…' : decision.replace('_', ' ')}
              </div>
              {isPending && (
                <div className="w-5 h-5 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
              )}
              {isUnavailable && (
                <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wide">
                  LLM deliberation unavailable · showing initial Council analysis
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
              Confidence
            </div>
            <div className="text-2xl font-black text-[var(--app-text)] italic" data-testid="council-decision-confidence">
              {isPending ? '…' : confText}
            </div>
            {d?.timeHorizon && !isPending && (
              <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                Horizon: {d.timeHorizon}
              </div>
            )}
          </div>
        </div>

        {/* Reason */}
        {(d?.decisionReason || isPending) && (
          <div className="bg-[var(--input-bg)] rounded-2xl p-5 border border-[var(--card-header-border)]">
            <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-2">Why</h4>
            <p className="text-sm text-[var(--app-text)] font-medium leading-relaxed" data-testid="council-decision-reason">
              {isPending
                ? 'The Council is deliberating across all four provider views — agreements, disagreements, and Perplexity live research are being synthesized now.'
                : (d?.decisionReason || 'Decision reasoning unavailable.')}
            </p>
          </div>
        )}

        {/* Agree / Disagree grid */}
        {!isPending && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Agreements */}
            <div>
              <h4 className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-3">
                Provider Agreement
              </h4>
              {(d?.agreements?.length ?? 0) > 0 ? (
                <ul className="space-y-2" data-testid="council-decision-agreements">
                  {d!.agreements!.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-[11px] text-[var(--muted-text)] font-medium leading-relaxed">
                      <CheckCircle2 size={12} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[11px] text-[var(--muted-text)] italic">No explicit cross-provider agreements identified.</p>
              )}
            </div>

            {/* Disagreements */}
            <div>
              <h4 className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-3">
                Provider Disagreement
              </h4>
              {(d?.disagreements?.length ?? 0) > 0 ? (
                <ul className="space-y-2" data-testid="council-decision-disagreements">
                  {d!.disagreements!.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-[11px] text-[var(--muted-text)] font-medium leading-relaxed">
                      <AlertTriangle size={12} className="text-amber-500 mt-0.5 flex-shrink-0" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[11px] text-[var(--muted-text)] italic">Providers did not surface material disagreements.</p>
              )}
            </div>
          </div>
        )}

        {/* Supporting evidence + Missing data */}
        {!isPending && ((d?.supportingEvidence?.length ?? 0) > 0 || (d?.missingData?.length ?? 0) > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {(d?.supportingEvidence?.length ?? 0) > 0 && (
              <div>
                <h4 className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-3">
                  Supporting Evidence
                </h4>
                <ul className="space-y-2" data-testid="council-decision-evidence">
                  {d!.supportingEvidence!.map((e, i) => (
                    <li key={i} className="text-[11px] text-[var(--muted-text)] font-medium leading-relaxed pl-3 border-l-2 border-blue-500/30">
                      {e}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(d?.missingData?.length ?? 0) > 0 && (
              <div>
                <h4 className="text-[10px] font-black text-rose-600 uppercase tracking-widest mb-3">
                  Missing Data
                </h4>
                <ul className="space-y-2" data-testid="council-decision-missing">
                  {d!.missingData!.map((m, i) => (
                    <li key={i} className="text-[11px] text-[var(--muted-text)] font-medium leading-relaxed pl-3 border-l-2 border-rose-500/30">
                      {m}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Perplexity Research */}
        {!isPending && d?.researchContext && (
          <div className="bg-purple-500/5 border border-purple-500/20 rounded-2xl p-5">
            <h4 className="text-[10px] font-black text-purple-600 uppercase tracking-widest mb-2">
              Perplexity Live Research
            </h4>
            <p className="text-[11px] text-[var(--muted-text)] font-medium leading-relaxed" data-testid="council-decision-research">
              {d.researchContext}
            </p>
          </div>
        )}

        {/* Next trigger / what would change decision */}
        {!isPending && (d?.whatWouldChangeDecision?.length ?? 0) > 0 && (
          <div>
            <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-3">
              Next Trigger — What Would Change This Decision
            </h4>
            <ul className="space-y-1.5" data-testid="council-decision-triggers">
              {d!.whatWouldChangeDecision!.map((w, i) => (
                <li key={i} className="text-[11px] text-[var(--muted-text)] font-medium leading-relaxed">
                  • {w}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Advisory-only footer */}
        <div className="border-t border-[var(--card-header-border)] pt-4">
          <p className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest leading-snug">
            {d?.advisoryNote ||
              'ADVISORY ONLY. Council recommendations are research output, not trade execution. Live trading is BLOCKED.'}
          </p>
          {d?.deliberationProvider && d?.deliberationProvider !== 'pending' && (
            <p className="text-[9px] font-medium text-[var(--muted-text)] mt-1">
              Deliberator: {d.deliberationProvider}
              {typeof pollAttempts === 'number' && pollAttempts > 0 ? ` · ${pollAttempts} poll${pollAttempts === 1 ? '' : 's'}` : ''}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const CouncilResultPanel = ({
  result,
  liveDeliberation,
}: {
  result: NormalizedCouncilResult;
  liveDeliberation?: CouncilDeliberation | null;
}) => {
  const insights = result?.insights ?? [];
  const risks = result?.risks ?? [];
  const recommendations = result?.recommendations ?? [];
  const confidence = result?.confidence ?? 0;
  // 2026-05-20 council-repair: once the async deliberator has reported a
  // completed decision, promote its confidence / decisionReason / final_decision
  // into this top-of-page panel so the "Analysis Summary" stops showing the
  // pre-deliberation readout. We still always render the per-provider cards
  // below (Council Insights) so the user can see the underlying views.
  const deliberated =
    liveDeliberation && liveDeliberation.status === 'completed' ? liveDeliberation : null;
  const effectiveConfidence =
    deliberated && typeof deliberated.confidence === 'number' ? deliberated.confidence : confidence;
  const confidencePercent =
    effectiveConfidence > 1 ? Math.round(effectiveConfidence) : Math.round(effectiveConfidence * 100);
  const deliberatedDecision = deliberated?.finalDecision;
  const deliberatedReason = deliberated?.decisionReason;
  const isDeliberating =
    !deliberated && (liveDeliberation?.status === 'pending' || (!liveDeliberation && !!result?.deliberationRequestId));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="council-gateway-results"
      className="bg-[var(--card-bg)] border-2 border-emerald-500/30 rounded-[2rem] p-8 shadow-2xl relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full -mr-32 -mt-32 blur-3xl" />

      <div className="relative z-10 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center">
              <Atom size={20} className="text-emerald-500" />
            </div>
            <div>
              <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic tracking-tight">Gateway Council Analysis</h3>
              <p className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                ID: {result?.analysisId ?? 'N/A'} • {safeFormatTimestamp(result?.timestamp, { fallback: '', invalidFallback: '' })}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Confidence</div>
            <div data-testid="council-gateway-confidence" className="text-2xl font-black text-[var(--app-text)] italic">{confidencePercent}%</div>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-[var(--input-bg)] rounded-2xl p-6 border border-[var(--card-header-border)]">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Analysis Summary</h4>
            {deliberated && (
              <span
                data-testid="council-gateway-final-decision"
                className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-md border border-emerald-500/40 text-emerald-600 bg-emerald-500/10"
              >
                Council Decision: {deliberatedDecision} · {confidencePercent}%
              </span>
            )}
            {isDeliberating && (
              <span
                data-testid="council-gateway-deliberating"
                className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-md border border-amber-500/40 text-amber-600 bg-amber-500/10"
              >
                Deliberating…
              </span>
            )}
          </div>
          {/* 2026-05-20 council-repair: prefer the deliberated decisionReason
              over the initial pre-deliberation readout once /deliberate
              completes. While the deliberation is still pending, show the
              initial Council readout. Either way, this is NEVER the bare
              "Full Council — N of M providers reporting" preamble that used
              to leak through. */}
          <p data-testid="council-gateway-summary" className="text-sm text-[var(--app-text)] font-medium leading-relaxed whitespace-pre-line">
            {deliberatedReason
              || (isDeliberating
                  ? `${result?.summary ?? 'Initial Council readout — providers have submitted their views.'}\n\nFinal Council deliberation is in progress. The decision, agreement / disagreement summary, and research context will replace this text within seconds.`
                  : (result?.summary ?? 'No summary available.'))}
          </p>
        </div>

        {/* Insights + Risks Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Insights */}
          <div>
            <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-4">Council Insights</h4>
            <div className="space-y-3">
              {insights.length > 0 ? (
                insights.map((insight, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-[var(--input-bg)] rounded-xl border border-[var(--input-border)]">
                    <div className="w-6 h-6 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Cpu size={12} className="text-emerald-500" />
                    </div>
                    <div>
                      <span className="text-[10px] font-black text-emerald-600 uppercase">{insight?.agent ?? 'Agent'}</span>
                      <p className="text-[11px] text-[var(--muted-text)] font-medium mt-0.5">{insight?.content ?? ''}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-[11px] text-[var(--muted-text)] font-medium italic">No insights available.</p>
              )}
            </div>
          </div>

          {/* Risks */}
          <div>
            <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-4">Risk Assessment</h4>
            <div className="space-y-3">
              {risks.length > 0 ? (
                risks.map((risk, i) => (
                  <div key={i} className={cn(
                    "flex items-start gap-3 p-3 rounded-xl border",
                    risk?.severity === 'high' ? "bg-rose-500/5 border-rose-500/20" :
                    risk?.severity === 'medium' ? "bg-amber-500/5 border-amber-500/20" :
                    "bg-[var(--input-bg)] border-[var(--input-border)]"
                  )}>
                    <AlertTriangle size={14} className={cn(
                      "mt-0.5 flex-shrink-0",
                      risk?.severity === 'high' ? "text-rose-500" :
                      risk?.severity === 'medium' ? "text-amber-500" :
                      "text-[var(--muted-text)]"
                    )} />
                    <div>
                      <span className={cn(
                        "text-[10px] font-black uppercase",
                        risk?.severity === 'high' ? "text-rose-600" :
                        risk?.severity === 'medium' ? "text-amber-600" :
                        "text-[var(--muted-text)]"
                      )}>
                        {risk?.severity ?? 'unknown'} risk
                      </span>
                      <p className="text-[11px] text-[var(--muted-text)] font-medium mt-0.5">{risk?.label ?? ''}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-[11px] text-[var(--muted-text)] font-medium italic">No risks identified.</p>
              )}
            </div>
          </div>
        </div>

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div>
            <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-4">Recommendations</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {recommendations.map((rec, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-[var(--input-bg)] rounded-xl border border-[var(--input-border)]">
                  <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                  <p className="text-[11px] text-[var(--muted-text)] font-medium">{rec ?? ''}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
//  COUNCIL STATUS BANNER
// ═══════════════════════════════════════════════════════════════════════

const CouncilStatusBanner = ({ council }: { council: ReturnType<typeof useCouncilStore> }) => {
  if (council.isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="council-loading-banner"
        className="bg-blue-500/10 border border-blue-500/20 rounded-2xl px-6 py-4 flex items-center gap-4"
      >
        <div className="w-8 h-8 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
        <div>
          <p className="text-xs font-black text-blue-600 uppercase italic">Running AI Council Analysis...</p>
          <p className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Consulting gateway agents</p>
        </div>
      </motion.div>
    );
  }

  if (council.isRetrying) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="council-retrying-banner"
        className="bg-amber-500/10 border border-amber-500/20 rounded-2xl px-6 py-4 flex items-center gap-4"
      >
        <RefreshCw size={20} className="text-amber-500 animate-spin" />
        <div>
          <p className="text-xs font-black text-amber-600 uppercase italic">Retrying Analysis...</p>
          <p className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
            Attempt {Math.min((council.retryCount ?? 0) + 1, 3)} of 3
          </p>
        </div>
      </motion.div>
    );
  }

  if (council.isError) {
    // Detect intentional-restricted states from the message body so the UI
    // reads as "Council Restricted" (amber, informational) rather than a
    // generic red "Analysis failed". Backend returns this for accounts
    // where the Council pipeline is not enabled.
    const msg = (council.error ?? '').toLowerCase();
    const isRestricted =
      msg.includes('restricted on this account') ||
      msg.includes('temporarily unavailable') ||
      msg.includes('currently unavailable') ||
      msg.includes('not available on this account') ||
      msg.includes('not enabled on this account') ||
      msg.includes('not signed in to council');
    if (isRestricted) {
      return (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="council-restricted-banner"
          className="bg-amber-500/10 border border-amber-500/20 rounded-2xl px-6 py-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-4">
            <Info size={20} className="text-amber-500" />
            <div>
              <p className="text-xs font-black text-amber-600 uppercase italic">Council analysis restricted</p>
              <p className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                {council.error ?? 'Council analysis is not enabled on this account.'}
              </p>
            </div>
          </div>
        </motion.div>
      );
    }
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="council-error-banner"
        className="bg-rose-500/10 border border-rose-500/20 rounded-2xl px-6 py-4 flex items-center justify-between"
      >
        <div className="flex items-center gap-4">
          <XCircle size={20} className="text-rose-500" />
          <div>
            <p className="text-xs font-black text-rose-600 uppercase italic">Analysis failed. Please retry.</p>
            <p className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
              {council.error ?? 'Unknown error'}
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  if (council.isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="council-success-banner"
        className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl px-6 py-4 flex items-center gap-4"
      >
        <CheckCircle2 size={20} className="text-emerald-500" />
        <div>
          <p className="text-xs font-black text-emerald-600 uppercase italic">Council Analysis Complete</p>
          <p className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
            {council.currentAnalysis?.insights?.length ?? 0} insights • {council.currentAnalysis?.recommendations?.length ?? 0} recommendations
          </p>
        </div>
      </motion.div>
    );
  }

  return null;
};

// ═══════════════════════════════════════════════════════════════════════
//  DEBATE CARD (preserved from original)
// ═══════════════════════════════════════════════════════════════════════

const DebateCard = ({ card, isExpanded, onToggle }: { card: TranscriptCard, isExpanded: boolean, onToggle: () => void }) => {
  const config = MODEL_CONFIG[card.modelName as keyof typeof MODEL_CONFIG] || {
    color: 'text-[var(--muted-text)] bg-[var(--input-bg)] border-[var(--card-border)]',
    icon: Atom,
    tooltip: card.modelName
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl shadow-sm overflow-hidden mb-4"
    >
      <div className="px-5 py-4 border-b border-[var(--card-header-border)] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative group/icon">
            <div className="inline-flex items-center justify-center transition-transform group-hover/icon:scale-110">
              <config.icon size={22} />
            </div>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-[var(--app-text)] text-[var(--card-bg)] text-[10px] font-bold rounded opacity-0 group-hover/icon:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
              {config.tooltip}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-black text-[var(--app-text)] uppercase italic">{card.modelName}</span>
              <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider", config.color)}>
                {card.role}
              </span>
            </div>
            <div className="text-[10px] text-[var(--muted-text)] font-bold uppercase tracking-widest mt-0.5">
              Stage: {card.stage} • {card.timestamp}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest mb-1">Confidence</div>
            <div className="w-20 h-1.5 bg-[var(--input-bg)] rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500"
                style={{ width: `${card.confidence * 100}%` }}
              />
            </div>
          </div>
          <button
            onClick={onToggle}
            className="p-1.5 text-[var(--muted-text)] hover:text-[var(--app-text)] transition-colors"
          >
            <Maximize2 size={16} />
          </button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <div>
          <h4 className="text-xs font-black text-[var(--muted-text)] uppercase tracking-widest mb-2">Statement</h4>
          <p className="text-sm text-[var(--app-text)] font-medium leading-relaxed">
            {card.content}
          </p>
        </div>

        <div className="bg-[var(--input-bg)] rounded-xl p-4 border border-[var(--input-border)]">
          <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Plain Language</h4>
          <p className="text-xs text-[var(--muted-text)] italic leading-relaxed">
            {card.plainLanguageExplanation}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-2">Evidence</h4>
            <ul className="space-y-1.5">
              {(card?.evidenceUsed ?? []).map((evidence, i) => (
                <li key={i} className="flex items-start gap-2 text-[11px] text-[var(--muted-text)] font-medium">
                  <CheckCircle2 size={12} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                  {evidence}
                </li>
              ))}
            </ul>
          </div>
          {card.riskFlag && (
            <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl">
              <div className="flex items-center gap-2 text-rose-500 mb-1.5">
                <AlertTriangle size={14} />
                <span className="text-[10px] font-black uppercase italic">Risk Flag</span>
              </div>
              <p className="text-[11px] text-rose-600 font-medium leading-relaxed">
                {card.riskFlag}
              </p>
            </div>
          )}
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden pt-4 border-t border-[var(--card-header-border)] space-y-4"
            >
              <div>
                <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-2">Deep Reasoning</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase">Probability Estimates</span>
                      <p className="text-xs text-[var(--app-text)] font-medium mt-1">{card?.deepReasoning?.probabilityEstimates ?? ''}</p>
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase">Scenario Analysis</span>
                      <p className="text-xs text-[var(--app-text)] font-medium mt-1">{card?.deepReasoning?.scenarioAnalysis ?? ''}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase">Technical References</span>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {(card?.deepReasoning?.technicalReferences ?? []).map((ref, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-[var(--input-bg)] rounded text-[10px] font-bold text-[var(--muted-text)] uppercase">{ref}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase">Caveats</span>
                      <ul className="space-y-1 mt-1">
                        {(card?.deepReasoning?.caveats ?? []).map((caveat, i) => (
                          <li key={i} className="text-[10px] text-[var(--muted-text)] font-medium">• {caveat}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════

export default function AICouncilAnalysisCenter() {
  const [symbol, setSymbol] = useState('');
  const [debateConfig, setDebateConfig] = useState({
    assetType: 'Stocks',
    debateType: 'Full Council Debate',
    timeframe: '1 Day'
  });
  const [isDebating, setIsDebating] = useState(false);
  const [currentStage, setCurrentStage] = useState<DebateStage>('INTAKE');
  const [currentAnalysis, setCurrentAnalysis] = useState<CouncilAnalysis | null>(null);
  const [visibleCards, setVisibleCards] = useState<TranscriptCard[]>([]);
  const [expandedCardIndex, setExpandedCardIndex] = useState<number | null>(null);
  const [history, setHistory] = useState<CouncilAnalysis[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('All Debates');
  const [debateMode, setDebateMode] = useState('Standard');
  const [notification, setNotification] = useState<{ message: string, type: 'success' | 'error' } | null>(null);

  // Council Store (Gateway integration)
  const council = useCouncilStore();
  // 2026-05-20 council-repair: track the latest deliberation state lifted up
  // from CouncilDecisionCard so the top-of-page Analysis Summary can promote
  // the deliberated decisionReason once /deliberate completes.
  const [liveDeliberation, setLiveDeliberation] = useState<CouncilDeliberation | null>(null);
  useEffect(() => {
    // Reset the lifted state whenever the user runs a new analysis so the
    // old completed deliberation doesn't bleed across requests.
    setLiveDeliberation(null);
  }, [council.currentAnalysis?.analysisId]);

  const feedEndRef = useRef<HTMLDivElement>(null);

  // Track last request for retry
  const lastRequestRef = useRef<{ symbol: string; assetType: string; debateType: string; timeframe: string } | null>(null);

  // Load history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('adversyn_debate_history');
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory));
      } catch {
        // Corrupted storage — ignore
      }
    }
  }, []);

  // Save history to localStorage
  useEffect(() => {
    if (history.length > 0) {
      localStorage.setItem('adversyn_debate_history', JSON.stringify(history));
    }
  }, [history]);

  // Handle sequential card appearance
  useEffect(() => {
    if (currentAnalysis && isDebating) {
      let cardIndex = 0;
      const interval = setInterval(() => {
        if (cardIndex < (currentAnalysis?.transcript?.length ?? 0)) {
          const card = currentAnalysis.transcript[cardIndex];
          setVisibleCards(prev => [...prev, card]);

          const stage = (card?.stage ?? '').toUpperCase() as DebateStage;
          if (STAGES.includes(stage)) {
            setCurrentStage(stage);
          }

          cardIndex++;
        } else {
          clearInterval(interval);
          setIsDebating(false);
          setCurrentStage('DECISION');
          setHistory(prev => [currentAnalysis, ...prev]);
        }
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [currentAnalysis]);

  // Reset window scroll when the Council page mounts so navigation always
  // lands at the top, regardless of where the previous page was scrolled.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll to bottom of feed when new cards arrive. Skip the initial mount
  // (visibleCards === []) so the page does not jump to the bottom on entry.
  useEffect(() => {
    if (visibleCards.length === 0) return;
    feedEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [visibleCards]);

  const [isAnalyzingSymbol, setIsAnalyzingSymbol] = useState<string | null>(null);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // ── Gateway Council Analysis (via store/adapter) ──
  const handleGatewayAnalysis = useCallback(async () => {
    if (!symbol.trim()) return;

    const request = {
      symbol: symbol.trim(),
      assetType: debateConfig.assetType,
      debateType: debateConfig.debateType,
      timeframe: debateConfig.timeframe,
    };
    lastRequestRef.current = request;

    logInteraction('AICouncil', 'Gateway Analysis', request);
    await council.runAnalysis(request);
  }, [symbol, debateConfig, council]);

  // ── Retry handler ──
  const handleRetry = useCallback(async () => {
    if (!lastRequestRef.current) return;
    logInteraction('AICouncil', 'Retry Gateway Analysis');
    council.resetAnalysis();
    await council.runAnalysis(lastRequestRef.current);
  }, [council]);

  // ── Original Gemini debate (unchanged) ──
  const runCouncilAnalysisLocal = async (config: { symbol: string, assetType: string, debateType: string, timeframe: string }) => {
    if (!config.symbol.trim()) {
      console.warn("Cannot initiate debate: Symbol is empty");
      return;
    }

    console.log("Initiating AI Council Analysis with config:", config);
    setIsDebating(true);
    setIsAnalyzingSymbol(config.symbol);
    setCurrentStage('INTAKE');
    setVisibleCards([]);
    setCurrentAnalysis(null);
    setExpandedCardIndex(null);

    try {
      const result = await runCouncilDebate(
        config.symbol,
        config.assetType,
        config.debateType,
        config.timeframe
      );
      setCurrentAnalysis(result);
      setIsAnalyzingSymbol(null);
    } catch (error) {
      console.error("Debate failed:", error);
      setIsDebating(false);
      setIsAnalyzingSymbol(null);
    }
  };

  // ── Initiate Debate: runs BOTH gateway + Gemini in parallel ──
  const handleInitiateDebate = useCallback(() => {
    const trimmed = symbol.trim();
    if (!trimmed) return;

    // Ensure progress always resets to step 1 (Intake) for a NEW debate.
    council.resetAnalysis();
    setVisibleCards([]);
    setCurrentAnalysis(null);
    setExpandedCardIndex(null);
    setCurrentStage('INTAKE');

    // Run gateway analysis via store. This is the SOLE source of
    // truth for the Council page now -- the backend orchestrator
    // calls GPT, Claude, Gemini, and Perplexity in parallel and
    // returns a structured contract. The parallel frontend-direct
    // Gemini debate path is intentionally disabled (it tripped on
    // "Tool call context circulation is not enabled for models/
    // gemini-2.5-flash" and is redundant with the backend orchestrator).
    handleGatewayAnalysis();

    // (Disabled 2026-05-15) Original Gemini direct debate -- see comment
    // above. AI Tutor is unaffected; this only removes the parallel
    // Gemini call on the Council debate page.
    // runCouncilAnalysisLocal({
    //   symbol: trimmed,
    //   ...debateConfig
    // });
  }, [symbol, debateConfig, handleGatewayAnalysis, council]);

  const filteredHistory = history.filter(item => {
    const matchesSearch = (item?.symbol ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    if (filter === 'All Debates') return matchesSearch;
    if (filter === 'Bullish') return matchesSearch && item?.synthesis?.verdict === 'Bullish';
    if (filter === 'Bearish') return matchesSearch && item?.synthesis?.verdict === 'Bearish';
    if (filter === 'High Confidence') return matchesSearch && ((item?.synthesis?.confidenceScore ?? 0) >= 0.8 || (item?.synthesis?.confidenceScore ?? 0) >= 80);
    return matchesSearch;
  });

  return (
    <div data-testid="council-debate-root" className="min-h-screen bg-[var(--app-bg)] pb-20">
      {/* Notification Toast */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20, x: '-50%' }}
            animate={{ opacity: 1, y: 20, x: '-50%' }}
            exit={{ opacity: 0, y: -20, x: '-50%' }}
            className={cn(
              "fixed top-4 left-1/2 z-50 px-6 py-3 rounded-2xl shadow-2xl border flex items-center gap-3 min-w-[300px]",
              notification.type === 'success' ? "bg-emerald-600 border-emerald-500 text-white" : "bg-rose-600 border-rose-500 text-white"
            )}
          >
            {notification.type === 'success' ? <CheckCircle2 size={18} /> : <Info size={18} />}
            <span className="text-xs font-black uppercase italic tracking-widest">{notification.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top Header / Control Bar */}
      <div className="bg-[var(--card-bg)] border-b border-[var(--card-border)] sticky top-0 z-20 px-6 py-4">
        <div className="app-container space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-4xl font-black text-[var(--app-text)] tracking-tight uppercase italic font-display">AI Council War Room</h1>
              <p className="text-xs font-bold text-[var(--muted-text)] uppercase tracking-widest mt-1 italic">Transparent multi-model trading analysis and decision reasoning.</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  logInteraction('AICouncil', 'Load Previous Debates');
                  setNotification({ message: "Loading previous debate sessions...", type: 'success' });
                }}
                className="px-4 py-2 bg-[var(--input-bg)] hover:bg-[var(--card-border)] text-[var(--muted-text)] rounded-xl text-xs font-black uppercase italic transition-all active:scale-95"
              >
                Load Previous
              </button>
              <button
                onClick={() => {
                  logInteraction('AICouncil', 'Compare Past Debates');
                  setNotification({ message: "Entering comparison mode...", type: 'success' });
                }}
                className="px-4 py-2 bg-[var(--input-bg)] hover:bg-[var(--card-border)] text-[var(--muted-text)] rounded-xl text-xs font-black uppercase italic transition-all active:scale-95"
              >
                Compare Past
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative">
              <input
                type="text"
                data-testid="council-input-symbol"
                placeholder="Enter Symbol (Example: NVDA)"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                className="w-full bg-[var(--input-bg)] border border-[var(--card-border)] rounded-xl px-4 py-2.5 text-sm font-bold text-[var(--app-text)] outline-none focus:border-emerald-500 transition-all"
              />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-text)]" size={18} />
            </div>

            <select
              data-testid="council-select-asset-type"
              value={debateConfig.assetType}
              onChange={(e) => {
                logInteraction('AICouncil', 'Change Asset Type', { type: e.target.value });
                setDebateConfig(prev => ({ ...prev, assetType: e.target.value }));
              }}
              className="bg-[var(--input-bg)] border border-[var(--card-border)] rounded-xl px-4 py-2.5 text-sm font-bold text-[var(--app-text)] outline-none focus:border-emerald-500 transition-all cursor-pointer"
            >
              {['Stocks', 'ETF', 'Crypto', 'Options', 'Futures'].map(opt => (
                <option key={opt} value={opt} className="bg-[var(--card-bg)]">{opt}</option>
              ))}
            </select>

            <select
              data-testid="council-select-debate-type"
              value={debateConfig.debateType}
              onChange={(e) => {
                logInteraction('AICouncil', 'Change Debate Mode', { mode: e.target.value });
                setDebateConfig(prev => ({ ...prev, debateType: e.target.value }));
              }}
              className="bg-[var(--input-bg)] border border-[var(--card-border)] rounded-xl px-4 py-2.5 text-sm font-bold text-[var(--app-text)] outline-none focus:border-emerald-500 transition-all cursor-pointer"
            >
              {['Quick Analysis', 'Full Council Debate', 'Risk-Focused Debate', 'Momentum Setup Review', 'Institutional Flow Review'].map(opt => (
                <option key={opt} value={opt} className="bg-[var(--card-bg)]">{opt}</option>
              ))}
            </select>

            <div className="flex bg-[var(--input-bg)] p-1 rounded-xl border border-[var(--card-border)]">
              {['Intraday', '1 Day', '1 Week', '1 Month'].map(t => (
                <button
                  key={t}
                  data-testid="council-btn-timeframe"
                  onClick={() => {
                    if (debateConfig.timeframe === t) return;
                    setDebateConfig(prev => ({ ...prev, timeframe: t }));
                  }}
                  className={cn(
                    "flex-1 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase italic transition-all border",
                    debateConfig.timeframe === t
                      ? "bg-[var(--card-bg)] text-emerald-600 shadow-[0_0_10px_rgba(16,185,129,0.1)] border-emerald-500/30"
                      : "text-[var(--muted-text)] hover:text-[var(--app-text)] border-transparent"
                  )}
                >
                  {t}
                </button>
              ))}
            </div>

            <button
              onClick={handleInitiateDebate}
              disabled={isDebating || !symbol || council.isLoading || council.isRetrying}
              data-testid="council-btn-initiate-debate"
              className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-200 text-white rounded-xl px-6 py-2.5 text-sm font-black uppercase italic transition-all shadow-lg shadow-emerald-600/20 flex items-center justify-center gap-2"
            >
              {(isDebating || council.isLoading) ? <Loader2 size={18} className="animate-spin" /> : <Play size={18} className="fill-white" />}
              Initiate Debate
            </button>
          </div>

          {/* Debate Stage Tracker */}
          <div className="flex items-center justify-between pt-2">
            {STAGES.map((stage, idx) => {
              const isActive = currentStage === stage;
              const isCompleted = STAGES.indexOf(currentStage) > idx;

              return (
                <React.Fragment key={stage}>
                  <div className="flex flex-col items-center gap-2 flex-1">
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-black transition-all duration-500",
                      isActive ? "bg-emerald-600 text-white shadow-lg shadow-emerald-600/30 scale-110 animate-pulse" :
                      isCompleted ? "bg-emerald-600/10 text-emerald-600" :
                      "bg-[var(--input-bg)] text-[var(--muted-text)]"
                    )}>
                      {isCompleted ? <CheckCircle2 size={16} /> : idx + 1}
                    </div>
                    <span className={cn(
                      "text-[9px] font-black uppercase tracking-widest",
                      isActive ? "text-emerald-600" : isCompleted ? "text-[var(--app-text)]" : "text-[var(--muted-text)]"
                    )}>
                      {stage}
                    </span>
                  </div>
                  {idx < STAGES.length - 1 && (
                    <div className={cn(
                      "h-px flex-1 mx-2 transition-all duration-500",
                      isCompleted ? "bg-emerald-600/20" : "bg-[var(--card-border)]"
                    )} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Gateway Council Status Banner */}
      <div className="app-container pt-4">
        <CouncilStatusBanner council={council} />

        {/* Error state retry button */}
        {council.isError && (
          <div className="mt-3 flex items-center gap-3">
            <button
              data-testid="council-btn-retry"
              onClick={handleRetry}
              className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-xl text-xs font-black uppercase italic hover:bg-rose-500 transition-all active:scale-95"
            >
              <RefreshCw size={14} /> Retry Analysis
            </button>
            <button
              onClick={() => council.resetAnalysis()}
              className="px-4 py-2 bg-[var(--input-bg)] text-[var(--muted-text)] rounded-xl text-xs font-black uppercase italic hover:bg-[var(--card-border)] transition-all active:scale-95"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      {/* Gateway Results Panel */}
      {council.isSuccess && council.currentAnalysis && (
        <div className="app-container pt-6 space-y-6">
          <CouncilResultPanel
            result={council.currentAnalysis}
            liveDeliberation={liveDeliberation}
          />
          {/* 2026-05-19 two-phase async deliberation card */}
          <CouncilDecisionCard
            analysisId={council.currentAnalysis.analysisId}
            deliberationRequestId={council.currentAnalysis.deliberationRequestId}
            seedDeliberation={council.currentAnalysis.deliberation ?? null}
            onDeliberationUpdate={setLiveDeliberation}
          />
        </div>
      )}

      {/* Main Layout */}
      <div className="app-container py-8 grid grid-cols-1 lg:grid-cols-[280px_1fr_320px] gap-8">

        {/* Left Column — Debate History */}
        <aside className="space-y-6">
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <History size={18} className="text-emerald-600" />
              <h3 className="text-xs font-black text-[var(--app-text)] uppercase tracking-widest italic">Debate History</h3>
            </div>

            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Search debates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[var(--input-bg)] border border-[var(--card-border)] rounded-xl pl-9 pr-4 py-2 text-xs font-bold text-[var(--app-text)] outline-none focus:border-emerald-500 transition-all"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-text)]" size={14} />
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {['All Debates', 'Bullish', 'Bearish', 'High Confidence'].map(f => (
                <button
                  key={f}
                  onClick={() => {
                    if (filter === f) return;
                    setFilter(f);
                  }}
                  className={cn(
                    "px-2.5 py-1 rounded-lg text-[9px] font-black uppercase italic transition-all border",
                    filter === f
                      ? "bg-emerald-600 text-white border-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.2)]"
                      : "bg-[var(--input-bg)] text-[var(--muted-text)] border-[var(--input-border)] hover:bg-[var(--card-border)] hover:text-[var(--app-text)]"
                  )}
                >
                  {f}
                </button>
              ))}
            </div>

            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2 scrollbar-hide">
              {filteredHistory.length > 0 ? (
                filteredHistory.map((item, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setCurrentAnalysis(item);
                      setVisibleCards(item?.transcript ?? []);
                      setCurrentStage('DECISION');
                    }}
                    className="w-full text-left p-3 rounded-xl border border-[var(--card-header-border)] hover:border-emerald-500/30 hover:bg-emerald-600/5 transition-all group"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-black text-[var(--app-text)] italic group-hover:text-emerald-600 transition-colors">{item?.symbol ?? ''}</span>
                      <span className={cn(
                        "text-[9px] font-black px-1.5 py-0.5 rounded uppercase",
                        item?.synthesis?.verdict === 'Bullish' ? "bg-emerald-600/10 text-emerald-600" :
                        item?.synthesis?.verdict === 'Bearish' ? "bg-rose-600/10 text-rose-600" :
                        "bg-[var(--input-bg)] text-[var(--muted-text)]"
                      )}>
                        {item?.synthesis?.verdict ?? 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-[var(--muted-text)] font-bold uppercase tracking-widest">
                      <span>{safeFormatTimestamp(item?.started_at, { mode: 'date', fallback: '', invalidFallback: '' })}</span>
                      <span>Conf: {(((item?.synthesis?.confidenceScore ?? 0) > 1 ? (item?.synthesis?.confidenceScore ?? 0) : (item?.synthesis?.confidenceScore ?? 0) * 100)).toFixed(0)}%</span>
                    </div>
                  </button>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">No history found</p>
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* Center Column — Live Debate Feed */}
        <main className="space-y-6">
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5 shadow-sm min-h-[700px] flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Atom size={18} className="text-emerald-600" />
                <h3 className="text-xs font-black text-[var(--app-text)] uppercase tracking-widest italic">Live Council Debate</h3>
              </div>
              <div className="flex bg-[var(--input-bg)] p-1 rounded-lg border border-[var(--input-border)]">
                {['Standard', 'Beginner', 'Professional'].map(mode => (
                  <button
                    key={mode}
                    onClick={() => {
                      if (debateMode === mode) return;
                      logInteraction('AICouncil', 'Change Debate Mode', { mode });
                      setDebateMode(mode);
                    }}
                    className={cn(
                      "px-3 py-1 text-[9px] font-black uppercase italic transition-all rounded-md border",
                      debateMode === mode
                        ? "bg-[var(--card-bg)] text-emerald-600 shadow-[0_0_10px_rgba(16,185,129,0.1)] border-emerald-500/30"
                        : "text-[var(--muted-text)] hover:text-[var(--app-text)] border-transparent"
                    )}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 space-y-4">
              {visibleCards.length > 0 ? (
                visibleCards.map((card, i) => (
                  <DebateCard
                    key={i}
                    card={card}
                    isExpanded={expandedCardIndex === i}
                    onToggle={() => setExpandedCardIndex(expandedCardIndex === i ? null : i)}
                  />
                ))
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center space-y-6 opacity-50">
                  <div className="w-16 h-16 bg-[var(--input-bg)] rounded-2xl flex items-center justify-center">
                    <Atom size={32} className="text-[var(--muted-text)]" />
                  </div>
                  <div>
                    <h4 className="text-lg font-black text-[var(--app-text)] uppercase italic">Ready to Start a Council Debate</h4>
                    <p className="text-sm text-[var(--muted-text)] font-medium mt-1">Enter a symbol above and click Initiate Debate.</p>
                  </div>
                  <div className="flex gap-3">
                    {['NVDA', 'SPY', 'BTC', 'ETH', 'TSLA'].map(s => (
                      <button
                        key={s}
                        disabled={isDebating || council.isLoading}
                        onClick={() => {
                          setSymbol(s);
                          // Run gateway analysis
                          const request = {
                            symbol: s,
                            assetType: debateConfig.assetType,
                            debateType: debateConfig.debateType,
                            timeframe: debateConfig.timeframe,
                          };
                          lastRequestRef.current = request;
                          // Always reset before kicking off a new debate so
                          // the progress indicator never inherits a previous
                          // run's retryCount (avoids the "3 of 3" flash).
                          council.resetAnalysis();
                          setVisibleCards([]);
                          setCurrentAnalysis(null);
                          setExpandedCardIndex(null);
                          setCurrentStage('INTAKE');
                          council.runAnalysis(request);
                          // (Disabled 2026-05-15) Parallel Gemini debate;
                          // backend orchestrator now runs Gemini server-side
                          // alongside GPT/Claude/Perplexity.
                          // runCouncilAnalysisLocal({
                          //   symbol: s,
                          //   ...debateConfig
                          // });
                        }}
                        className={cn(
                          "px-4 py-2 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl text-xs font-black text-[var(--muted-text)] hover:border-emerald-500 hover:text-emerald-600 transition-all uppercase italic flex items-center gap-2",
                          isAnalyzingSymbol === s && "border-emerald-500 text-emerald-600"
                        )}
                      >
                        {isAnalyzingSymbol === s ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} className="text-amber-500" />}
                        Analyze {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div ref={feedEndRef} />
            </div>
          </div>
        </main>

        {/* Right Column — AI Council Members */}
        <aside className="space-y-6">
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-6">
              <Atom size={18} className="text-emerald-600" />
              <h3 className="text-xs font-black text-[var(--app-text)] uppercase tracking-widest italic">AI Council Members</h3>
            </div>

            <div className="space-y-4">
              {[
                { name: 'GPT', role: 'Market Analyst', specialty: 'Intelligence & Versatility', icon: BrainIcon, color: 'text-purple-600 bg-purple-600/10', tooltip: 'GPT — AI intelligence engine' },
                { name: 'Claude', role: 'Quantitative Analyst', specialty: 'Wisdom & Reasoning', icon: OwlIcon, color: 'text-amber-600 bg-amber-600/10', tooltip: 'Claude — AI reasoning engine' },
                { name: 'Gemini', role: 'Adversary', specialty: 'Memory & Knowledge', icon: ElephantIcon, color: 'text-blue-600 bg-blue-600/10', tooltip: 'Gemini — AI knowledge engine' },
                { name: 'Perplexity', role: 'Research Analyst', specialty: 'Search & Discovery', icon: CompassIcon, color: 'text-teal-600 bg-teal-600/10', tooltip: 'Perplexity — AI search engine' },
                { name: 'Synthesizer', role: 'Decision Engine', specialty: 'Final recommendation synthesis', icon: Zap, color: 'text-slate-600 bg-slate-600/10', tooltip: 'Synthesizer — Final decision engine' },
              ].map((member) => (
                <div key={member.name} className="p-4 rounded-2xl border border-[var(--card-header-border)] space-y-3 group/member">
                  <div className="flex items-center gap-3">
                    <div className="relative group/icon">
                      <div className="inline-flex items-center justify-center transition-transform group-hover/member:scale-110">
                        <member.icon size={28} />
                      </div>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-[var(--app-text)] text-[var(--card-bg)] text-[10px] font-bold rounded opacity-0 group-hover/icon:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                        {member.tooltip}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-black text-[var(--app-text)] uppercase italic">{member.name}</h4>
                      <p className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">{member.role}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      <span className="text-[9px] font-black text-emerald-600 uppercase">Active</span>
                    </div>
                    <span className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Conf: 85%</span>
                  </div>
                  <p className="text-[10px] text-[var(--muted-text)] font-medium leading-relaxed">
                    {member.specialty}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-[var(--card-header-border)]">
              <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-4">Role Guide</h4>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1 flex-shrink-0" />
                  <p className="text-[10px] text-[var(--muted-text)] font-medium leading-relaxed">
                    <span className="font-black text-[var(--app-text)] uppercase">Market Analyst</span> — Interprets price movement and macro trends.
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1 flex-shrink-0" />
                  <p className="text-[10px] text-[var(--muted-text)] font-medium leading-relaxed">
                    <span className="font-black text-[var(--app-text)] uppercase">Quantitative Analyst</span> — Evaluates probabilities using statistical models.
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-rose-500 mt-1 flex-shrink-0" />
                  <p className="text-[10px] text-[var(--muted-text)] font-medium leading-relaxed">
                    <span className="font-black text-[var(--app-text)] uppercase">Adversary</span> — Challenges the trade thesis and highlights risk scenarios.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Bottom Section — Final Debate Summary (original Gemini results) */}
        <section className="lg:col-span-3">
          <AnimatePresence>
            {currentAnalysis && currentStage === 'DECISION' && (
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="council-panel-results"
                className="bg-[var(--card-bg)] border-2 border-[var(--app-text)] rounded-[2rem] p-8 shadow-2xl relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full -mr-32 -mt-32 blur-3xl" />

                <div className="flex flex-col lg:flex-row gap-12 relative z-10">
                  {/* Verdict Section */}
                  <div className="lg:w-1/3 space-y-8">
                    <div>
                      <h3 className="text-xs font-black text-[var(--muted-text)] uppercase tracking-[0.2em] mb-4">Final Recommendation</h3>
                      <div className="flex items-center gap-4">
                        <div className={cn(
                          "px-6 py-3 rounded-2xl text-2xl font-black uppercase italic shadow-lg",
                          currentAnalysis?.synthesis?.verdict === 'Bullish' ? "bg-emerald-600 text-white shadow-emerald-600/20" :
                          currentAnalysis?.synthesis?.verdict === 'Bearish' ? "bg-rose-600 text-white shadow-rose-600/20" :
                          "bg-[var(--app-text)] text-[var(--card-bg)] shadow-lg"
                        )}>
                          {currentAnalysis?.synthesis?.verdict ?? 'N/A'}
                        </div>
                        <div>
                          <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Confidence</div>
                          <div className="text-xl font-black text-[var(--app-text)] italic">
                            {((currentAnalysis?.synthesis?.confidenceScore ?? 0) > 1
                              ? (currentAnalysis?.synthesis?.confidenceScore ?? 0)
                              : (currentAnalysis?.synthesis?.confidenceScore ?? 0) * 100
                            ).toFixed(0)}%
                          </div>
                        </div>
                      </div>
                    </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Direction</div>
                    <div className="text-sm font-black text-[var(--app-text)] uppercase italic">{currentAnalysis?.synthesis?.tradeDirection ?? 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Time Horizon</div>
                    <div className="text-sm font-black text-[var(--app-text)] uppercase italic">{currentAnalysis?.synthesis?.tradeStructure?.horizon ?? 'N/A'}</div>
                  </div>
                </div>

                <div className="p-6 bg-[var(--input-bg)] rounded-3xl border border-[var(--card-header-border)]">
                  <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-4">Trade Structure</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase">Entry Zone</span>
                      <span className="text-xs font-black text-[var(--app-text)] italic">{currentAnalysis?.synthesis?.tradeStructure?.entry ?? 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase">Stop Loss</span>
                      <span className="text-xs font-black text-rose-600 italic">{currentAnalysis?.synthesis?.tradeStructure?.stop ?? 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase">Profit Target</span>
                      <span className="text-xs font-black text-emerald-600 italic">{currentAnalysis?.synthesis?.tradeStructure?.target ?? 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase">Position Size</span>
                      <span className="text-xs font-black text-[var(--app-text)] italic">{currentAnalysis?.synthesis?.tradeStructure?.size ?? 'N/A'}</span>
                    </div>
                  </div>
                </div>
                  </div>

                  {/* Arguments Section */}
                  <div className="lg:w-2/3 grid grid-cols-1 md:grid-cols-2 gap-12">
                    <div className="space-y-6">
                      <div className="flex items-center gap-2 text-emerald-600">
                        <TrendingUp size={18} />
                        <h4 className="text-xs font-black uppercase italic">Bullish Arguments</h4>
                      </div>
                      <ul className="space-y-4">
                        {(currentAnalysis?.synthesis?.bullishArguments ?? []).map((arg, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                            <p className="text-xs text-[var(--muted-text)] font-medium leading-relaxed">{arg}</p>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="space-y-6">
                      <div className="flex items-center gap-2 text-rose-600">
                        <TrendingDown size={18} />
                        <h4 className="text-xs font-black uppercase italic">Bearish Arguments</h4>
                      </div>
                      <ul className="space-y-4">
                        {(currentAnalysis?.synthesis?.bearishArguments ?? []).map((arg, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-rose-500 mt-1.5 flex-shrink-0" />
                            <p className="text-xs text-[var(--muted-text)] font-medium leading-relaxed">{arg}</p>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="md:col-span-2 pt-8 border-t border-[var(--card-header-border)]">
                      <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-6">Decision Quality Metrics</h4>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        <div>
                          <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest mb-2">Consensus Strength</div>
                          <div className="text-sm font-black text-[var(--app-text)] italic">{currentAnalysis?.synthesis?.decisionQuality?.consensusStrength ?? 'N/A'}</div>
                        </div>
                        <div>
                          <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest mb-2">Conflict Level</div>
                          <div className="text-sm font-black text-[var(--app-text)] italic">{currentAnalysis?.synthesis?.decisionQuality?.conflictLevel ?? 'N/A'}</div>
                        </div>
                        <div>
                          <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest mb-2">Evidence Completeness</div>
                          <div className="text-sm font-black text-[var(--app-text)] italic">{currentAnalysis?.synthesis?.decisionQuality?.evidenceCompleteness ?? 'N/A'}</div>
                        </div>
                        <div>
                          <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest mb-2">Risk Grade</div>
                          <div className={cn(
                            "text-xl font-black italic",
                            ['A', 'B'].includes(currentAnalysis?.synthesis?.decisionQuality?.riskGrade ?? '') ? "text-emerald-600" : "text-rose-600"
                          )}>
                            {currentAnalysis?.synthesis?.decisionQuality?.riskGrade ?? 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Post-Debate Actions */}
                <div className="mt-12 pt-8 border-t border-[var(--card-header-border)] flex flex-wrap items-center justify-between gap-6">
                  <div className="flex items-center gap-3">
                    <button
                      data-testid="council-btn-save-debate"
                      onClick={() => {
                        logInteraction('AICouncil', 'Save Debate', { symbol: currentAnalysis?.symbol });
                        setNotification({ message: "Debate saved to institutional archive.", type: 'success' });
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-[var(--app-text)] text-[var(--card-bg)] rounded-xl text-xs font-black uppercase italic hover:opacity-90 transition-all active:scale-95"
                    >
                      <Save size={14} /> Save Debate
                    </button>
                    <button
                      data-testid="council-btn-journal"
                      onClick={() => {
                        logInteraction('AICouncil', 'Journal Debate', { symbol: currentAnalysis?.symbol });
                        setNotification({ message: "Added to trading journal.", type: 'success' });
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-[var(--input-bg)] text-[var(--muted-text)] rounded-xl text-xs font-black uppercase italic hover:bg-[var(--card-border)] transition-all active:scale-95"
                    >
                      <BookMarked size={14} /> Journal
                    </button>
                    <button
                      onClick={() => {
                        logInteraction('AICouncil', 'Set Alert', { symbol: currentAnalysis?.symbol });
                        setNotification({ message: "Price alert configured based on council targets.", type: 'success' });
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-[var(--input-bg)] text-[var(--muted-text)] rounded-xl text-xs font-black uppercase italic hover:bg-[var(--card-border)] transition-all active:scale-95"
                    >
                      <Bell size={14} /> Alert
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      data-testid="council-btn-export"
                      onClick={() => {
                        logInteraction('AICouncil', 'Export Debate', { symbol: currentAnalysis?.symbol });
                        setNotification({ message: "Generating PDF report...", type: 'success' });
                      }}
                      className="flex items-center gap-2 px-4 py-2 border border-[var(--card-border)] text-[var(--muted-text)] rounded-xl text-xs font-black uppercase italic hover:bg-[var(--input-bg)] transition-all active:scale-95"
                    >
                      <Download size={14} /> Export
                    </button>
                    <button
                      onClick={() => {
                        logInteraction('AICouncil', 'Paper Trade Setup', { symbol: currentAnalysis?.symbol });
                        setNotification({ message: "Forwarding to Paper Trading module...", type: 'success' });
                      }}
                      className="flex items-center gap-2 px-6 py-2 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase italic hover:bg-emerald-500 transition-all shadow-lg shadow-emerald-600/20 active:scale-95"
                    >
                      Paper Trade Setup <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
      </div>
    </div>
  );
}
